import React, { useState, useRef } from 'react';
import { X, AlertCircle, User, Calendar, MapPin, Camera, Link2 } from 'lucide-react';
import type { FamilyMember } from '../../types/family';
import { useFamilyStore } from '../../store/familyStore';
import { useAuthStore } from '../../store/authStore';

const buildInitialFormData = (editingMember?: FamilyMember): FormData => {
  if (!editingMember) return initialFormData;
  return {
    name: editingMember.name ?? '',
    nickname: editingMember.nickname ?? '',
    gender: (editingMember.gender ?? 'male') as FormData['gender'],
    birthDate: editingMember.birthDate ?? '',
    birthPlace: editingMember.birthPlace ?? '',
    isAlive: editingMember.isAlive ?? true,
    deathDate: editingMember.deathDate ?? '',
    photoUrl: editingMember.photoUrl ?? '',
    facebookUrl: editingMember.facebookUrl ?? '',
    role: 'myself',
  };
};

interface UnifiedMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  editingMember?: FamilyMember;
  familyTreeName?: string;
  isFirstMember?: boolean;
}

interface FormData {
  name: string;
  nickname: string;
  gender: 'male' | 'female';
  birthDate: string;
  birthPlace: string;
  isAlive: boolean;
  deathDate: string;
  photoUrl: string;
  facebookUrl: string;
  role?: 'myself' | 'parent' | 'grandparent' | 'other'; // Only for first member
}

const initialFormData: FormData = {
  name: '',
  nickname: '',
  gender: 'male',
  birthDate: '',
  birthPlace: '',
  isAlive: true,
  deathDate: '',
  photoUrl: '',
  facebookUrl: '',
  role: 'myself'
};

export const UnifiedMemberModal: React.FC<UnifiedMemberModalProps> = ({
  isOpen,
  onClose,
  editingMember,
  familyTreeName,
  isFirstMember = false
}) => {
  const { addMember, updateMember } = useFamilyStore();
  const { token } = useAuthStore();
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be under 5MB');
      return;
    }

    // Preview the selection locally; the actual upload to R2 happens on
    // submit (once we have a member id).
    setSelectedPhoto(file);
    const reader = new FileReader();
    reader.onload = () => {
      updateFormData({ photoUrl: reader.result as string });
    };
    reader.readAsDataURL(file);
  };

  /** Upload the selected photo to R2 and return the stored URL. */
  const uploadPhoto = async (memberId: string): Promise<string> => {
    if (!selectedPhoto) return '';
    const apiBase = import.meta.env.VITE_API_BASE_URL?.replace('/api/v1', '');
    const form = new FormData();
    form.append('photo', selectedPhoto);
    const res = await fetch(`${apiBase}/members/${memberId}/photo`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Photo upload failed');
    }
    const data = await res.json();
    return data.photoUrl;
  };

  // Reset form state when the modal opens or the editing target changes.
  // Using the setState-during-render pattern (guarded by a ref) avoids
  // cascading renders from a syncing effect.
  const prevSignature = useRef<string | null>(null);
  const signature = `${isOpen ? 'open' : 'closed'}:${editingMember?.id ?? 'new'}`;
  if (prevSignature.current !== signature) {
    prevSignature.current = signature;
    setFormData(buildInitialFormData(editingMember));
    setError('');
  }

  if (!isOpen) return null;

  const updateFormData = (updates: Partial<FormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
    setError('');
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError('Full name is required');
      return false;
    }

    if (formData.name.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return false;
    }

    if (formData.birthDate) {
      const birthDate = new Date(formData.birthDate);
      if (isNaN(birthDate.getTime())) {
        setError('Invalid birth date format');
        return false;
      }
    }

    if (!formData.isAlive && formData.deathDate) {
      const deathDate = new Date(formData.deathDate);
      const birthDate = new Date(formData.birthDate);
      if (birthDate > deathDate) {
        setError('Death date cannot be earlier than birth date');
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      // If a photo was selected, upload it to R2 first (needs member id).
      // For edit mode the member exists; for add mode we create then upload.
      const memberData: Partial<FamilyMember> = {
        name: formData.name.trim(),
        nickname: formData.nickname.trim() || undefined,
        gender: formData.gender,
        birthDate: formData.birthDate || undefined,
        birthPlace: formData.birthPlace.trim() || undefined,
        isAlive: formData.isAlive,
        deathDate: !formData.isAlive ? formData.deathDate || undefined : undefined,
        facebookUrl: formData.facebookUrl.trim() || undefined,
      };

      if (editingMember) {
        // Upload photo (if any) and get the R2 URL before updating.
        if (selectedPhoto) {
          memberData.photoUrl = await uploadPhoto(editingMember.id);
        }
        // Update existing member
        updateMember(editingMember.id, memberData);
      } else {
        // Add new member first, then upload the photo to R2.
        const newMember: FamilyMember = {
          id: `member-${crypto.randomUUID()}`,
          name: formData.name.trim(),
          nickname: formData.nickname.trim() || undefined,
          gender: formData.gender,
          birthDate: formData.birthDate,
          birthPlace: formData.birthPlace.trim() || undefined,
          isAlive: formData.isAlive,
          deathDate: !formData.isAlive ? formData.deathDate || undefined : undefined,
          facebookUrl: formData.facebookUrl.trim() || undefined,
          // Smart generation assignment based on role for first member
          generation: isFirstMember ? getGenerationFromRole(formData.role) : 1,
          maritalStatus: 'single' // Default marital status
        };
        await addMember(newMember);
        if (selectedPhoto) {
          const photoUrl = await uploadPhoto(newMember.id);
          if (photoUrl) updateMember(newMember.id, { photoUrl });
        }
      }
      
      // Close modal and reset form
      handleClose();
      
    } catch (err) {
      console.error('Error saving member:', err);
      setError('Failed to save member data. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to determine generation based on role
  const getGenerationFromRole = (role?: string): number => {
    switch (role) {
      case 'grandparent': return 1;
      case 'parent': return 2;
      case 'myself': return 3;
      default: return 1; // Default to root generation
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setFormData(initialFormData);
      setSelectedPhoto(null);
      setError('');
    }
  };

  const getModalTitle = () => {
    if (editingMember) return 'Edit Family Member';
    if (isFirstMember) return 'First Family Member';
    return 'Add Family Member';
  };

  const getModalSubtitle = () => {
    if (isFirstMember && familyTreeName) return familyTreeName;
    if (editingMember) return 'Edit family member details';
    return 'Enter new member details';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-in fade-in slide-in-from-bottom-4 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 sticky top-0 bg-white rounded-t-xl">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{getModalTitle()}</h2>
              <p className="text-sm text-gray-500">{getModalSubtitle()}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          {/* First Member Context */}
          {isFirstMember && (
            <div className="mb-6 text-center">
              <p className="text-gray-700 mb-4">
                Who will be the <strong>"root"</strong> of this family tree?
              </p>
            </div>
          )}

          <div className="space-y-6">
            {/* Profile Photo */}
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border border-gray-200">
                  {formData.photoUrl ? (
                    <img src={formData.photoUrl} alt={formData.name} className="w-full h-full object-cover" />
                  ) : (
                    <User className="w-8 h-8 text-gray-400" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  className="absolute bottom-0 right-0 w-7 h-7 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors disabled:opacity-50"
                  aria-label="Change profile photo"
                >
                  <Camera className="w-4 h-4" />
                </button>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">Profile Photo</p>
                <p className="text-xs text-gray-500 mb-2">JPG, PNG or GIF. Max 5MB.</p>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 transition-colors disabled:opacity-50"
                >
                  Upload Photo
                </button>
                {formData.photoUrl && (
                  <button
                    type="button"
                    onClick={() => updateFormData({ photoUrl: '' })}
                    disabled={isSubmitting}
                    className="ml-2 px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 transition-colors disabled:opacity-50"
                  >
                    Remove
                  </button>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                className="hidden"
              />
            </div>

            {/* Basic Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Full Name */}
              <div className="md:col-span-2">
                <label htmlFor="member-name" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="member-name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => updateFormData({ name: e.target.value })}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${
                    error && !formData.name.trim() ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="e.g., John Smith, Jane Doe"
                  disabled={isSubmitting}
                  autoFocus
                />
              </div>

              {/* Nickname */}
              <div>
                <label htmlFor="nickname" className="block text-sm font-medium text-gray-700 mb-2">
                  Nickname
                </label>
                <input
                  id="nickname"
                  type="text"
                  value={formData.nickname}
                  onChange={(e) => updateFormData({ nickname: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nickname or alias"
                  disabled={isSubmitting}
                />
              </div>

              {/* Gender */}
              <fieldset>
                <legend className="block text-sm font-medium text-gray-700 mb-2">
                  Gender <span className="text-red-500">*</span>
                </legend>
                <div className="flex space-x-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="male"
                      checked={formData.gender === 'male'}
                      onChange={(e) => updateFormData({ gender: e.target.value as 'male' | 'female' })}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-700">Male</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      value="female"
                      checked={formData.gender === 'female'}
                      onChange={(e) => updateFormData({ gender: e.target.value as 'male' | 'female' })}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-700">Female</span>
                  </label>
                </div>
              </fieldset>

              {/* Birth Date */}
              <div>
                <label htmlFor="birth-date" className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Date
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="birth-date"
                    type="date"
                    value={formData.birthDate}
                    onChange={(e) => updateFormData({ birthDate: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={isSubmitting}
                  />
                </div>
                
                {/* Birth year only checkbox removed - not in core data */}
              </div>

              {/* Birth Place */}
              <div>
                <label htmlFor="birth-place" className="block text-sm font-medium text-gray-700 mb-2">
                  Birth Place
                </label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="birth-place"
                    type="text"
                    value={formData.birthPlace}
                    onChange={(e) => updateFormData({ birthPlace: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="City/Town"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Living Status */}
              <fieldset className="md:col-span-2">
                <legend className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </legend>
                <div className="flex space-x-4 mb-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.isAlive}
                      onChange={() => updateFormData({ isAlive: true, deathDate: '' })}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-700">Living</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={!formData.isAlive}
                      onChange={() => updateFormData({ isAlive: false })}
                      className="mr-2 text-blue-600 focus:ring-blue-500"
                      disabled={isSubmitting}
                    />
                    <span className="text-sm text-gray-700">Deceased</span>
                  </label>
                </div>

                {/* Death Date */}
                {!formData.isAlive && (
                  <div>
                    <label htmlFor="death-date" className="block text-sm font-medium text-gray-700 mb-2">
                      Death Date
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        id="death-date"
                        type="date"
                        value={formData.deathDate}
                        onChange={(e) => updateFormData({ deathDate: e.target.value })}
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                )}
              </fieldset>

              {/* Facebook Profile */}
              <div className="md:col-span-2">
                <label htmlFor="facebook-url" className="block text-sm font-medium text-gray-700 mb-2">
                  Facebook Profile URL
                </label>
                <div className="relative">
                  <Link2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    id="facebook-url"
                    type="url"
                    value={formData.facebookUrl}
                    onChange={(e) => updateFormData({ facebookUrl: e.target.value })}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="https://facebook.com/username"
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Role Selection - Only for First Member */}
              {isFirstMember && (
                <div className="md:col-span-2">
                  <fieldset>
                    <legend className="block text-sm font-medium text-gray-700 mb-3">
                      Role:
                    </legend>
                    <div className="space-y-3">
                      {[
                        { value: 'myself', label: 'Myself', desc: 'I will be the center of the family tree' },
                        { value: 'parent', label: 'My parent', desc: 'Father or mother as the family root' },
                        { value: 'grandparent', label: 'Grandparent', desc: 'Oldest known generation' },
                        { value: 'other', label: 'Other', desc: 'Another family member' }
                      ].map((option) => (
                        <label
                          key={option.value}
                          className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                            formData.role === option.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                          aria-label={`${option.label}: ${option.desc}`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={option.value}
                            checked={formData.role === option.value}
                            onChange={(e) => updateFormData({ role: e.target.value as FormData['role'] })}
                            className="mt-1 text-blue-600 focus:ring-blue-500"
                            disabled={isSubmitting}
                            aria-describedby={`role-desc-${option.value}`}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{option.label}</div>
                            <div className="text-sm text-gray-500" id={`role-desc-${option.value}`}>{option.desc}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                  </fieldset>
                </div>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center space-x-2 text-red-600 bg-red-50 border border-red-200 rounded-lg p-3">
                <AlertCircle className="w-4 h-4" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50"
              >
                CANCEL
              </button>
              <button
                type="submit"
                disabled={isSubmitting || !formData.name.trim()}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium flex items-center space-x-2 min-w-30 justify-center"
              >
                {isSubmitting && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                )}
                <span>
                  {(() => {
                    if (isSubmitting) return 'SAVING...';
                    if (editingMember) return 'UPDATE';
                    return 'SAVE';
                  })()}
                </span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};
